import isNil from 'lodash/isNil';
import { ISignificanceAnalysis } from '../apollo/responseTypes';
import {
  IHypothesisFieldsFragment,
  IHypothesisStatsDataFieldsFragment,
  ISignificanceDataFieldsFragment,
  QueryType,
} from '../apollo/graphqlSchema';
import {
  IParsedChartDataSignificance,
  ISignificanceControlStats,
  ISignificanceTableData,
  ISignificanceVariantStats,
} from '../../components/Chart/interfaces';
import { assertNotNil } from '../types';
import { freeze } from '@reduxjs/toolkit';

const getControlStats = (
  nullHypothesis: IHypothesisFieldsFragment,
): ISignificanceControlStats => {
  const { sampleSize, mean, standardDeviation, variant } = nullHypothesis;

  return {
    sampleSize: sampleSize.toString(),
    mean,
    standardDeviation,
    variant,
  };
};

const getVariantStats = (
  hypothesisStat: IHypothesisStatsDataFieldsFragment,
  hypothesis: IHypothesisFieldsFragment,
): ISignificanceVariantStats => {
  const {
    standardError,
    delta,
    lift,
    pValue,
    statisticalPower,
    confidenceInterval,
    testScore,
    statisticalSignificance,
    executedSimulations,
  } = hypothesisStat;
  const { sampleSize, mean, standardDeviation, variant } = hypothesis;

  return {
    variant,
    sampleSize: sampleSize.toString(),
    mean,
    standardDeviation,
    standardError,
    delta,
    lift,
    pValue,
    statisticalPower,
    statisticalSignificance,
    testScore,
    confidenceInterval,
    performanceResult: lift > 0 ? 'positive' : 'negative',
    executedSimulations: executedSimulations.toString(),
  };
};

const getLeadingVariant = (
  relPerfA: number,
  relPerfB?: number,
): ISignificanceTableData['leadingVariant'] => {
  if (isNil(relPerfB)) {
    if (relPerfA > 0) {
      return 'variantA';
    }
    return;
  }

  if (relPerfA < 0 && relPerfB < 0) {
    return;
  }

  return relPerfA > relPerfB ? 'variantA' : 'variantB';
};

const defaultSignificanceTableData = freeze<ISignificanceTableData>(
  {
    metricName: '',
    control: null,
    variantA: null,
    variantB: null,
  },
  true,
);

const generateSignificanceTable = (
  data: ISignificanceDataFieldsFragment,
): ISignificanceTableData => {
  const { hypotheses, hypothesisResults, metric, nullHypothesis, isEmpty } =
    data;

  if (isEmpty) {
    return defaultSignificanceTableData;
  }

  let error;

  const { variantA, variantB } = hypothesisResults.reduce<
    Pick<ISignificanceTableData, 'variantA' | 'variantB'>
  >(
    (acc, hypothesisResult) => {
      const { hypothesesStats, nullHypothesisStats } = hypothesisResult;

      if (nullHypothesisStats.errorMessage) {
        error = nullHypothesisStats.errorMessage;
      }

      hypothesesStats.forEach((hypothesesStat, index) => {
        const hypothesis = hypotheses[index];
        assertNotNil(hypothesis);
        const stats = getVariantStats(hypothesesStat, hypothesis);

        if (index === 0) {
          acc.variantA = stats;
        } else if (index === 1) {
          acc.variantB = stats;
        } else {
          throw Error("Index can't be more than 1");
        }

        if (!nullHypothesisStats.errorMessage && hypothesesStat.errorMessage) {
          error = hypothesesStat.errorMessage;
        }
      });

      return acc;
    },
    {
      variantA: null,
      variantB: null,
    },
  );

  assertNotNil(variantA);

  const leadingVariant = getLeadingVariant(variantA.lift, variantB?.lift);

  return {
    metricName: metric,
    control: getControlStats(nullHypothesis),
    variantA,
    variantB,
    leadingVariant,
    error,
  };
};

const transformSignificanceData = (
  analysis: ISignificanceAnalysis,
): IParsedChartDataSignificance => {
  assertNotNil(analysis.data);

  const data = generateSignificanceTable(analysis.data);

  return {
    queryType: QueryType.Significance,
    data,
  };
};

export default transformSignificanceData;
